import React, { useState } from "react";
import { Box, Button, Card, Icon, Alert, Badge, Dropdown, Spinner, Grid } from "~/components-shared";
import styled from "styled-components";
import { usePostHog } from "posthog-js/react";
import { useTranslation } from "react-i18next";
import { AuthoritiesEnum, InsightsLineItemItemTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import { useDispatch, useSelector } from "react-redux";
import insightsSelectors from "~/store/insights/selectors";
import insightsActionCreators from "~/store/insights/actions";
import toastsActionCreators from "~/store/toasts/actions";
import useAbacExt from "~/hooks/abac";
import Markdown from "~/components/Markdown";
const StyledBox = styled(Box)`
  position: absolute;
  top: 5px;
  right: 5px;
`;
const StyledTopicButton = styled(Button)`
  & span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const RoundIcon = styled(Icon)`
  padding: 2px;
  border-radius: 50%;
  border: 1px solid ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.success.main;
}};
`;
const AchievementItemCard = _ref2 => {
  let {
    showDismissed,
    isEditable = false,
    item,
    ...rest
  } = _ref2;
  const {
    t
  } = useTranslation();
  const posthog = usePostHog();
  const [itemState, setItemState] = useState(item);
  const dismissStatus = useSelector(insightsSelectors.getDismissStatusByLineItem(item.id));
  const deleteStatus = useSelector(insightsSelectors.getDeleteStatusByLineItem(item.id));
  const dismissError = useSelector(insightsSelectors.getDismissErrorByLineItem(item.id));
  const deleteError = useSelector(insightsSelectors.getDeleteErrorByLineItem(item.id));
  const error = dismissError || deleteError;
  const dispatch = useDispatch();
  const {
    userHas
  } = useAbacExt();
  if (!itemState || !showDismissed && itemState.isDismissed) {
    return null;
  }
  const handleToggleDismiss = () => {
    if (itemState.isDismissed) {
      dispatch(insightsActionCreators.restoreLineItemStarted(item.id, _ref3 => {
        let {
          data
        } = _ref3;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("AchievementItemCard.successfullyRestored", {
              name: item.summary
            }),
            timeout: 10000
          }));
          setItemState(data);
          posthog.capture(`Insights Highlights – Restore`, {
            lineItemId: item.id
          });
        }
      }));
    } else {
      dispatch(insightsActionCreators.dismissLineItemStarted(item.id, _ref4 => {
        let {
          data
        } = _ref4;
        if (data) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("AchievementItemCard.successfullyDismissed", {
              name: item.summary
            }),
            timeout: 10000
          }));
          setItemState(data);
          posthog.capture(`Insights Highlights – Dismiss`, {
            lineItemId: item.id
          });
        }
      }));
    }
  };
  const handleDelete = () => {
    dispatch(insightsActionCreators.deleteLineItemStarted(item.id, _ref5 => {
      let {
        data
      } = _ref5;
      if (data) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("AchievementItemCard.successfullyDeleted", {
            name: item.summary
          }),
          timeout: 10000
        }));
        setItemState(undefined);
        posthog.capture(`Insights Highlights – Delete`, {
          lineItemid: item.id
        });
      }
    }));
  };
  const handleTopicClick = topicId => {
    const url = `${window.location.origin}${RoutesEnum.TOPIC.replace(":topicId", topicId)}?view=footprints`;
    window.open(url, "_blank");
    posthog.capture(`Insights Highlights – Click Topic`, {
      topicId
    });
  };
  return /*#__PURE__*/React.createElement(Card, rest, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), itemState.itemType === InsightsLineItemItemTypeEnum.TODO && !!itemState.closedAt && /*#__PURE__*/React.createElement(Badge, {
    color: "grey",
    s: {
      px: 3,
      py: 1,
      mb: 3
    }
  }, /*#__PURE__*/React.createElement(RoundIcon, {
    color: "success",
    src: "tick",
    scale: 4,
    s: {
      mt: -0.5,
      mr: 2
    }
  }), t("AchievementItemCard.completedTask")), itemState.isDismissed && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentDanger",
    s: {
      px: 3,
      py: 1,
      mb: 3
    }
  }, t("AchievementItemCard.dismissed")), /*#__PURE__*/React.createElement(Markdown, {
    s: {
      mb: 3,
      pr: 6
    },
    style: {
      flexGrow: 1
    },
    fontSize: 4,
    lineHeight: 6,
    text: itemState.summary
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr auto",
    align: "end",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 2
  }, itemState.topics[0] && /*#__PURE__*/React.createElement(StyledTopicButton, {
    key: itemState.topics[0].id,
    onClick: () => handleTopicClick(itemState.topics[0].id),
    fontSize: 3,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": itemState.topics[0].name,
    s: {
      px: 2,
      py: 1
    },
    maxWidth: itemState.topics.length > 1 ? "200px" : "280px",
    color: "translucentPrimary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "timelapse",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), itemState.topics[0].name), itemState.topics.length > 1 && /*#__PURE__*/React.createElement(Dropdown, {
    display: "inline-block",
    direction: "topLeft",
    handlerProps: {
      color: "translucent",
      fontSize: 3,
      s: {
        px: 2,
        py: 1
      }
    },
    handler: `+ ${itemState.topics.length - 1} more`
  }, _ref6 => {
    let {
      isOpen,
      onItemClick
    } = _ref6;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, itemState.topics.slice(1, 10).map(topic => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      key: topic.id,
      onClick: () => {
        onItemClick();
        handleTopicClick(topic.id);
      }
    }, topic.name)));
  })), isEditable && userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Dropdown, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.options"),
    display: "inline-block",
    direction: "bottomLeft",
    s: {
      mb: -2,
      mr: -2
    },
    handlerProps: {
      color: "transparent",
      rounded: true,
      s: {
        p: 2.5
      }
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      display: "block",
      src: "dots-vertical"
    })
  }, _ref7 => {
    let {
      isOpen,
      onItemClick
    } = _ref7;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        onItemClick();
        handleDelete();
      }
    }, deleteStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      scale: 4,
      color: "primary",
      s: {
        ml: 1
      }
    })));
  })), isEditable && /*#__PURE__*/React.createElement(StyledBox, {
    justify: "space-between"
  }, /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": itemState.isDismissed ? t("common.actions.restore") : t("common.actions.dismiss"),
    onClick: () => handleToggleDismiss(),
    color: "transparent",
    rounded: true,
    outline: false,
    loading: dismissStatus === StatusStateEnum.LOADING,
    s: {
      p: 2,
      mx: 0.5
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 5,
    src: itemState.isDismissed ? "refresh" : "cross",
    color: "text",
    display: "block"
  }))));
};
export default AchievementItemCard;